import { useEffect, useState } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Pencil, Save, X, Newspaper, ArrowLeftRight, RefreshCw, Upload,
} from "lucide-react";
import { Button } from "../ui/button";
import LlmRegenerateButton from "./LlmRegenerateButton";
import SeoMetaEditor from "./SeoMetaEditor";
import ViewLiveLink from "./ViewLiveLink";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const EMPTY_TRANSFER = {
  player: "",
  direction: "in",
  from_club: "",
  to_club: "West Ham United",
  fee: "Not Known",
  date: "",
  position: "",
  team: "men",
  season: "",
  source_url: "",
  notes: "",
  loan: false,
};

const EMPTY_NEWS = {
  title: "",
  body: "",
  image_url: "",
  source: "Editorial",
  source_url: "",
};

const TransfersAdmin = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("transfers");

  // ---------- Transfers ----------
  const [transfers, setTransfers] = useState([]);
  const [total, setTotal] = useState(0);
  const [tLoading, setTLoading] = useState(true);
  const [team, setTeam] = useState("men");
  const [season, setSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState(EMPTY_TRANSFER);
  const [tEditingId, setTEditingId] = useState(null);
  const [tSubmitting, setTSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);

  const loadTransfers = async () => {
    setTLoading(true);
    try {
      const params = { team, limit: 500 };
      if (season) params.season = season;
      const res = await axios.get(`${API}/transfers`, { params });
      setTransfers(res.data.transfers || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load transfers");
    } finally {
      setTLoading(false);
    }
  };

  const loadSeasons = async () => {
    try {
      const res = await axios.get(`${API}/transfers/seasons`);
      setSeasons(res.data.seasons || []);
    } catch (err) {
      console.error("TransfersAdmin: failed to load seasons list", err);
    }
  };

  useEffect(() => { loadSeasons(); }, []);
  useEffect(() => { loadTransfers(); }, [team, season]);

  const openCreateTransfer = () => {
    setTForm(EMPTY_TRANSFER);
    setTEditingId(null);
    setTOpen(true);
  };

  const openEditTransfer = (t) => {
    setTForm({
      player: t.player || "",
      direction: t.direction || "in",
      from_club: t.from_club || "",
      to_club: t.to_club || "",
      fee: t.fee || "Not Known",
      date: t.date || "",
      position: t.position || "",
      team: t.team || "men",
      season: t.season || "",
      source_url: t.source_url || "",
      notes: t.notes || "",
      loan: !!t.loan,
    });
    setTEditingId(t.id);
    setTOpen(true);
  };

  const submitTransfer = async () => {
    if (!tForm.player || !tForm.season) {
      toast.error("Player and season required");
      return;
    }
    setTSubmitting(true);
    try {
      // Auto-fill the West Ham side based on direction
      const payload = { ...tForm };
      if (payload.direction === "in" && !payload.to_club) payload.to_club = "West Ham United";
      if (payload.direction === "out" && !payload.from_club) payload.from_club = "West Ham United";

      if (tEditingId) {
        await axios.put(`${API}/admin/transfers/${tEditingId}`, payload, { headers });
        toast.success("Transfer updated");
      } else {
        await axios.post(`${API}/admin/transfers`, payload, { headers });
        toast.success("Transfer created");
      }
      setTOpen(false);
      loadTransfers();
      loadSeasons();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setTSubmitting(false);
    }
  };

  const deleteTransfer = async (id) => {
    if (!window.confirm("Delete this transfer?")) return;
    try {
      await axios.delete(`${API}/admin/transfers/${id}`, { headers });
      toast.success("Deleted");
      loadTransfers();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const runScrape = async (mode) => {
    setScraping(true);
    try {
      await axios.post(`${API}/admin/transfers/scrape?mode=${mode}`, {}, { headers });
      toast.success(
        mode === "full"
          ? "Full Wikipedia seed started in background. Refresh in a few minutes."
          : "Current-season sync complete."
      );
      if (mode === "current") loadTransfers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to scrape");
    } finally {
      setScraping(false);
    }
  };

  // ---------- Transfer news ----------
  const [news, setNews] = useState([]);
  const [nLoading, setNLoading] = useState(true);
  const [nOpen, setNOpen] = useState(false);
  const [nForm, setNForm] = useState(EMPTY_NEWS);
  const [nFile, setNFile] = useState(null);
  const [nEditingId, setNEditingId] = useState(null);
  const [nSubmitting, setNSubmitting] = useState(false);

  const loadNews = async () => {
    setNLoading(true);
    try {
      const res = await axios.get(`${API}/admin/transfer-news`, { headers });
      setNews(res.data.items || []);
    } catch {
      toast.error("Failed to load transfer news");
    } finally {
      setNLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "news") loadNews();
  }, [tab]);

  const openCreateNews = () => {
    setNForm(EMPTY_NEWS);
    setNFile(null);
    setNEditingId(null);
    setNOpen(true);
  };

  const openEditNews = (n) => {
    setNForm({
      title: n.title || "",
      body: n.body || "",
      image_url: n.image_url || "",
      source: n.source || "Editorial",
      source_url: n.source_url || "",
    });
    setNFile(null);
    setNEditingId(n.id);
    setNOpen(true);
  };

  const wordCount = (nForm.body || "").trim().split(/\s+/).filter(Boolean).length;

  const submitNews = async () => {
    if (!nForm.title || !nForm.body) {
      toast.error("Title and body required");
      return;
    }
    setNSubmitting(true);
    try {
      let imageUrl = (nForm.image_url || "").trim();
      if (nFile) {
        const formData = new FormData();
        formData.append("file", nFile);
        const uploadRes = await axios.post(`${API}/upload`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        imageUrl = `/api/files/${uploadRes.data.storage_path}`;
      }
      const payload = { ...nForm, image_url: imageUrl || null };
      if (nEditingId) {
        await axios.put(`${API}/admin/transfer-news/${nEditingId}`, payload, { headers });
        toast.success("News updated");
      } else {
        await axios.post(`${API}/admin/transfer-news`, payload, { headers });
        toast.success("News published");
      }
      setNOpen(false);
      loadNews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setNSubmitting(false);
    }
  };

  const deleteNews = async (id) => {
    if (!window.confirm("Delete this transfer-news article?")) return;
    try {
      await axios.delete(`${API}/admin/transfer-news/${id}`, { headers });
      toast.success("Deleted");
      loadNews();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="transfers-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold" style={{ fontFamily: "Barlow Condensed" }}>
          TRANSFERS
        </h2>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => runScrape("current")}
            disabled={scraping}
            data-testid="scrape-current-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${scraping ? "animate-spin" : ""}`} /> Sync current season
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => runScrape("full")}
            disabled={scraping}
            data-testid="scrape-full-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${scraping ? "animate-spin" : ""}`} /> Full Wikipedia re-seed
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {[
          { k: "transfers", label: `Transfers (${total})`, icon: ArrowLeftRight },
          { k: "news", label: "Transfer News", icon: Newspaper },
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === k
                ? "border-[#7C2C3B] text-[#7C2C3B]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${k}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* -------- Transfers tab -------- */}
      {tab === "transfers" && (
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              {[
                { k: "men", label: "Men's" },
                { k: "women", label: "Women's" },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`admin-transfers-team-${k}`}
                  onClick={() => setTeam(k)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    team === k
                      ? "bg-[#7C2C3B] text-white border-[#7C2C3B]"
                      : "bg-white text-slate-600 border-slate-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <select
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              data-testid="admin-transfers-season"
              className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white"
            >
              <option value="">All seasons</option>
              {seasons.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={openCreateTransfer}
              className="ml-auto bg-[#7C2C3B] hover:bg-[#63232F]"
              data-testid="add-transfer-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add transfer
            </Button>
          </div>

          {tLoading ? (
            <div className="flex justify-center py-10 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : transfers.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-500 text-sm">
              No transfers found.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Season</th>
                    <th className="text-left px-3 py-2 font-medium">Player</th>
                    <th className="text-left px-3 py-2 font-medium">Dir.</th>
                    <th className="text-left px-3 py-2 font-medium">From → To</th>
                    <th className="text-left px-3 py-2 font-medium">Fee</th>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((t) => (
                    <tr key={t.id} className="border-t border-slate-100" data-testid={`admin-transfer-row-${t.id}`}>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{t.season}</td>
                      <td className="px-3 py-2 font-medium text-[#0F172A]">{t.player}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${
                            t.direction === "in"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-rose-100 text-rose-700"
                          }`}
                        >
                          {t.direction}
                        </span>
                        {t.loan && (
                          <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-amber-100 text-amber-700">
                            Loan
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="text-slate-500">{t.from_club}</span> → <span className="font-medium">{t.to_club}</span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{t.fee}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{t.date_raw || t.date || ""}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditTransfer(t)}
                          className="text-slate-500 hover:text-[#2BAEE5] p-1"
                          data-testid={`edit-transfer-${t.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteTransfer(t.id)}
                          className="text-slate-500 hover:text-rose-500 p-1 ml-1"
                          data-testid={`delete-transfer-${t.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* -------- News tab -------- */}
      {tab === "news" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={openCreateNews}
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              data-testid="add-transfer-news-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add transfer news
            </Button>
          </div>

          {nLoading ? (
            <div className="flex justify-center py-10 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : news.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-500 text-sm">
              No editorial transfer news yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="admin-transfer-news-list">
              {news.map((n) => (
                <div
                  key={n.id}
                  className={`bg-white border rounded-lg p-3 flex items-start gap-3 ${
                    n.deleted ? "border-rose-200 opacity-60" : "border-slate-200"
                  }`}
                  data-testid={`admin-news-row-${n.id}`}
                >
                  {n.image_url && (
                    <img src={n.image_url} alt="" className="w-16 h-16 object-cover rounded" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-slate-500 mb-1">
                      <span className="font-semibold text-[#7C2C3B]">{n.source}</span>
                      {n.published_at && <> · {n.published_at.slice(0, 10)}</>}
                      {n.deleted && <span className="ml-2 text-rose-500 font-semibold">DELETED</span>}
                    </div>
                    <div className="text-sm font-semibold text-[#0F172A]">{n.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-2 mt-1">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">/transfers/news/{n.slug}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <ViewLiveLink slug={n.slug} path="/transfers/news" testId={`transfer-news-${n.id}`} />
                    <button
                      onClick={() => openEditNews(n)}
                      className="text-slate-500 hover:text-[#2BAEE5] p-1"
                      data-testid={`edit-news-${n.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {!n.deleted && (
                      <button
                        onClick={() => deleteNews(n.id)}
                        className="text-slate-500 hover:text-rose-500 p-1"
                        data-testid={`delete-news-${n.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* -------- Transfer form dialog -------- */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="max-w-lg" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>{tEditingId ? "Edit transfer" : "Add transfer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Player name"
              value={tForm.player}
              onChange={(e) => setTForm({ ...tForm, player: e.target.value })}
              data-testid="transfer-form-player"
            />
            <div className="grid grid-cols-2 gap-3">
              <select
                value={tForm.direction}
                onChange={(e) => setTForm({ ...tForm, direction: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="transfer-form-direction"
              >
                <option value="in">In</option>
                <option value="out">Out</option>
              </select>
              <select
                value={tForm.team}
                onChange={(e) => setTForm({ ...tForm, team: e.target.value })}
                className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="transfer-form-team"
              >
                <option value="men">Men's</option>
                <option value="women">Women's</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="From club"
                value={tForm.from_club}
                onChange={(e) => setTForm({ ...tForm, from_club: e.target.value })}
                data-testid="transfer-form-from"
              />
              <Input
                placeholder="To club"
                value={tForm.to_club}
                onChange={(e) => setTForm({ ...tForm, to_club: e.target.value })}
                data-testid="transfer-form-to"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Fee (or 'Not Known')"
                value={tForm.fee}
                onChange={(e) => setTForm({ ...tForm, fee: e.target.value })}
                data-testid="transfer-form-fee"
              />
              <Input
                placeholder="Position"
                value={tForm.position}
                onChange={(e) => setTForm({ ...tForm, position: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Date (YYYY-MM-DD)"
                value={tForm.date}
                onChange={(e) => setTForm({ ...tForm, date: e.target.value })}
                data-testid="transfer-form-date"
              />
              <Input
                placeholder="Season e.g. 2025-26"
                value={tForm.season}
                onChange={(e) => setTForm({ ...tForm, season: e.target.value })}
                data-testid="transfer-form-season"
              />
            </div>
            <Input
              placeholder="Source URL (optional)"
              value={tForm.source_url}
              onChange={(e) => setTForm({ ...tForm, source_url: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!tForm.loan}
                onChange={(e) => setTForm({ ...tForm, loan: e.target.checked })}
                data-testid="transfer-form-loan"
                className="w-4 h-4"
              />
              This is a <span className="font-semibold text-amber-700">loan deal</span> (not a permanent transfer)
            </label>
            <textarea
              placeholder="Notes (optional)"
              value={tForm.notes}
              onChange={(e) => setTForm({ ...tForm, notes: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              rows={2}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTOpen(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                onClick={submitTransfer}
                disabled={tSubmitting}
                className="bg-[#7C2C3B] hover:bg-[#63232F]"
                data-testid="transfer-form-submit"
              >
                {tSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* -------- News form dialog -------- */}
      <Dialog open={nOpen} onOpenChange={setNOpen}>
        <DialogContent className="max-w-xl" data-testid="news-dialog">
          <DialogHeader>
            <DialogTitle>{nEditingId ? "Edit transfer news" : "Add transfer news"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Headline (used as H1)"
              value={nForm.title}
              onChange={(e) => setNForm({ ...nForm, title: e.target.value })}
              data-testid="news-form-title"
            />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-500">Article body (LLM-editable · your edits never overwritten)</label>
                {nEditingId && (
                  <LlmRegenerateButton
                    collection="transfer_news"
                    docId={nEditingId}
                    onText={(text) => setNForm({ ...nForm, body: text })}
                    testId="transfers-regenerate-body"
                  />
                )}
              </div>
              <textarea
                placeholder="Article body (max 200 words)"
                value={nForm.body}
                onChange={(e) => setNForm({ ...nForm, body: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                rows={6}
                data-testid="news-form-body"
              />
              <div className={`text-[11px] mt-1 ${wordCount > 200 ? "text-rose-600" : "text-slate-400"}`}>
                {wordCount} / 200 words {wordCount > 200 && "(will be trimmed)"}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Source name"
                value={nForm.source}
                onChange={(e) => setNForm({ ...nForm, source: e.target.value })}
              />
              <Input
                placeholder="Source URL (optional)"
                value={nForm.source_url}
                onChange={(e) => setNForm({ ...nForm, source_url: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Image (paste URL or upload)</label>
              <Input
                placeholder="https://… (optional)"
                value={nForm.image_url}
                onChange={(e) => setNForm({ ...nForm, image_url: e.target.value })}
              />
              <div className="mt-2 flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded">
                  <Upload className="w-4 h-4" /> Upload file
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setNFile(e.target.files?.[0] || null)}
                    data-testid="news-form-file"
                  />
                </label>
                {nFile && <span className="text-xs text-slate-500 truncate">{nFile.name}</span>}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setNOpen(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                onClick={submitNews}
                disabled={nSubmitting}
                className="bg-[#7C2C3B] hover:bg-[#63232F]"
                data-testid="news-form-submit"
              >
                {nSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
            {/* SEO metadata for admin-authored transfer news editorials */}
            {nEditingId && (
              <SeoMetaEditor collection="transfer_news" docId={nEditingId} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransfersAdmin;
